/** Neural field behind the lobby. Ported from the operator's synaps lobby template. */

const SKIN = '#ff2d95';

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let running = false;
let raf = 0;
let width = 1;
let height = 1;
let dpr = 1;
let neurons: Array<{ x: number; y: number; vx: number; vy: number; r: number; ph: number }> = [];
const pulses: Array<{ a: number; b: number; p: number }> = [];
const mouse = { x: -999, y: -999, t: 0 };
const segments: Array<{ x: number; y: number }> = [];
const head = { x: 0, y: 0 };
let bound = false;

function seed(): void {
  const count = Math.round(Math.min(90, (width * height) / 14000));
  neurons = Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.25,
    vy: (Math.random() - 0.5) * 0.25,
    r: 1.5 + Math.random() * 2.5,
    ph: Math.random() * 6,
  }));
}

function size(): void {
  if (!canvas || !ctx) return;
  width = window.innerWidth;
  height = window.innerHeight;
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(width * dpr);
  canvas.height = Math.floor(height * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  head.x = width / 2;
  head.y = height / 2;
  if (segments.length === 0) {
    for (let i = 0; i < 46; i++) segments.push({ x: head.x, y: head.y });
  }
  seed();
}

function bind(): void {
  if (bound) return;
  bound = true;
  window.addEventListener('resize', size);
  window.addEventListener('pointermove', (ev) => {
    mouse.x = ev.clientX;
    mouse.y = ev.clientY;
    mouse.t = performance.now();
  });
  window.addEventListener('pointerleave', () => { mouse.t = 0; });
}

function snakeTarget(t: number): [number, number] {
  if (mouse.t && performance.now() - mouse.t < 2500) return [mouse.x, mouse.y];
  return [
    width / 2 + Math.cos(t * 0.0006) * width * 0.36,
    height / 2 + Math.sin(t * 0.0009) * height * 0.3 + Math.sin(t * 0.002) * 30,
  ];
}

function stepSnake(t: number): void {
  const [tx, ty] = snakeTarget(t);
  const dx = tx - head.x;
  const dy = ty - head.y;
  const dist = Math.hypot(dx, dy) || 1;
  const speed = Math.min(4.2, dist * 0.06 + 1.2);
  head.x += (dx / dist) * speed;
  head.y += (dy / dist) * speed;
  const first = segments[0];
  if (first) {
    first.x = head.x;
    first.y = head.y;
  }
  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1];
    const cur = segments[i];
    if (!prev || !cur) continue;
    const ddx = cur.x - prev.x;
    const ddy = cur.y - prev.y;
    const dd = Math.hypot(ddx, ddy) || 1;
    cur.x = prev.x + (ddx / dd) * 11;
    cur.y = prev.y + (ddy / dd) * 11;
  }
}

function drawSnake(context: CanvasRenderingContext2D): void {
  context.globalCompositeOperation = 'lighter';
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    if (!seg) continue;
    const k = 1 - i / segments.length;
    const r = 5 + k * 9;
    context.shadowColor = SKIN;
    context.shadowBlur = 22;
    context.fillStyle = SKIN;
    context.globalAlpha = 0.25 + k * 0.6;
    context.beginPath();
    context.arc(seg.x, seg.y, r, 0, Math.PI * 2);
    context.fill();
    context.shadowBlur = 0;
    context.fillStyle = '#fff';
    context.globalAlpha = 0.12 * k;
    context.beginPath();
    context.arc(seg.x - r * 0.25, seg.y - r * 0.25, r * 0.45, 0, Math.PI * 2);
    context.fill();
  }
  context.globalAlpha = 1;
  context.globalCompositeOperation = 'source-over';
  const nose = segments[3];
  const lead = segments[0];
  if (!nose || !lead) return;
  const angle = Math.atan2(lead.y - nose.y, lead.x - nose.x);
  for (const offset of [-0.6, 0.6]) {
    const ex = lead.x + Math.cos(angle + offset) * 8;
    const ey = lead.y + Math.sin(angle + offset) * 8;
    context.fillStyle = '#fff';
    context.shadowColor = '#fff';
    context.shadowBlur = 8;
    context.beginPath();
    context.arc(ex, ey, 4, 0, Math.PI * 2);
    context.fill();
    context.shadowBlur = 0;
    context.fillStyle = '#2a0219';
    context.beginPath();
    context.arc(ex + Math.cos(angle) * 1.5, ey + Math.sin(angle) * 1.5, 2, 0, Math.PI * 2);
    context.fill();
  }
}

function frame(t: number): void {
  if (!running || !ctx) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  ctx.clearRect(0, 0, width, height);
  const reach = 150;
  for (const n of neurons) {
    if (!reduce) {
      n.x += n.vx;
      n.y += n.vy;
    }
    if (n.x < 0 || n.x > width) n.vx *= -1;
    if (n.y < 0 || n.y > height) n.vy *= -1;
    const dx = mouse.x - n.x;
    const dy = mouse.y - n.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 220 && dist > 1) {
      n.x += (dx / dist) * 0.6;
      n.y += (dy / dist) * 0.6;
    }
  }
  ctx.lineWidth = 1;
  for (let i = 0; i < neurons.length; i++) {
    for (let j = i + 1; j < neurons.length; j++) {
      const a = neurons[i];
      const b = neurons[j];
      if (!a || !b) continue;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (dist < reach) {
        ctx.strokeStyle = `rgba(255,45,149,${(1 - dist / reach) * 0.35})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        if (!reduce && Math.random() < 0.0009 && pulses.length < 40) pulses.push({ a: i, b: j, p: 0 });
      }
    }
  }
  for (const n of neurons) {
    const glow = 0.6 + 0.4 * Math.sin(t * 0.002 + n.ph);
    ctx.shadowColor = SKIN;
    ctx.shadowBlur = 14;
    ctx.fillStyle = `rgba(255,120,190,${0.5 + glow * 0.5})`;
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.r * (0.8 + glow * 0.4), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.shadowBlur = 0;
  for (let i = pulses.length - 1; i >= 0; i--) {
    const pulse = pulses[i];
    if (!pulse) continue;
    pulse.p += 0.025;
    if (pulse.p >= 1) {
      pulses.splice(i, 1);
      continue;
    }
    const a = neurons[pulse.a];
    const b = neurons[pulse.b];
    if (!a || !b) continue;
    const x = a.x + (b.x - a.x) * pulse.p;
    const y = a.y + (b.y - a.y) * pulse.p;
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 16;
    ctx.fillStyle = '#ffe3f2';
    ctx.beginPath();
    ctx.arc(x, y, 2.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  }
  stepSnake(t);
  drawSnake(ctx);
  raf = requestAnimationFrame(frame);
}

export function startLobbyField(target: HTMLCanvasElement): void {
  canvas = target;
  ctx = target.getContext('2d');
  if (!ctx) return;
  bind();
  if (neurons.length === 0) size();
  if (running) return;
  running = true;
  raf = requestAnimationFrame(frame);
}

export function stopLobbyField(): void {
  running = false;
  cancelAnimationFrame(raf);
}
